import React, { useCallback, useMemo } from 'react';
import {
  FormProps,
  Form as CustomForm,
} from '../../../../components/form/Form';
import { FieldValues } from 'react-hook-form';
import { collectFormDataExtraFields, recordTypeFormKey } from './logic';
import { useExtraFieldsFormValidationByFormId } from './useExtraFieldsFormValidation';
import { useDefaultRecordTypeName } from './useDefaultRecordTypeName';
import {
  ExtraFieldContextParam,
  ExtraFieldsFormId,
  ExtraFieldsSupportedObject,
} from './types';
import { ExtraFieldInternalValue } from './extraFieldInternalValue';
type Props<T extends FieldValues> = Omit<FormProps<T>, 'onSubmit'> & {
  onSubmit: (data: T, extraFields: ExtraFieldInternalValue[]) => void;
  objectApiName: ExtraFieldsSupportedObject;
  contextParams?: ExtraFieldContextParam[];
  formId: ExtraFieldsFormId;
};

export const ExtraFieldsForm = <T extends FieldValues>({
  objectApiName,
  contextParams,
  onSubmit,
  validationSchema,
  formId,
  defaultValues: propsDefaultValues,
  ...restProps
}: Props<T>) => {
  const { applyExtraFieldsValidationSchema } =
    useExtraFieldsFormValidationByFormId({
      objectApiName,
      formId,
      contextParams: contextParams ?? [],
    });

  const defaultRecordTypeName = useDefaultRecordTypeName(objectApiName);

  const handleSubmit = useCallback(
    (formData: T) => {
      const extraFields = collectFormDataExtraFields(formData);
      return onSubmit(formData, extraFields);
    },
    [onSubmit],
  );

  const defaultValues: any = useMemo(() => {
    // Only set defaultRecordTypeName if not already set in propsDefaultValues
    if (
      defaultRecordTypeName != null &&
      !propsDefaultValues?.[recordTypeFormKey]
    ) {
      return {
        ...(propsDefaultValues ?? {}),
        [recordTypeFormKey]: defaultRecordTypeName,
      };
    }
    return propsDefaultValues;
  }, [defaultRecordTypeName, propsDefaultValues]);

  return (
    <CustomForm
      onSubmit={handleSubmit}
      isChangeable={true} // allow to set default values
      defaultValues={defaultValues}
      validationSchema={applyExtraFieldsValidationSchema(validationSchema)}
      {...restProps}></CustomForm>
  );
};
